import { formatBytes } from "@repo/shared/helpers/format-bytes";
import {
  MAX_PUBLISH_RESULT_BYTES,
  MAX_RESULT_BYTES,
} from "@repo/shared/types/dataset-run-status";
import {
  type QueryFunctionContext,
  type QueryKey,
  queryOptions,
} from "@tanstack/react-query";

import { logger } from "~/libs/logger";
import { queryClient } from "~/libs/query-client";
import { trpc } from "~/libs/trpc.lib";
import duckdb from "~/workers/duckdb";
import {
  type CachedRunArrow,
  getCachedRunArrow,
  isRunCacheSupported,
  streamRunArrowToOpfs,
  writeRunArrowToOpfs,
} from "~/workers/opfs-dataset-cache";

const RUN_PAYLOAD_QUERY_PREFIX = "run-payload";
const RUN_PAYLOAD_GC_MS = 30 * 60_000;

/** Floor timeout for a run download, used for small or unknown-size payloads. */
const RUN_FETCH_TIMEOUT_MIN_MS = 30_000;
/**
 * Ceiling timeout, so a stalled download still fails in bounded time. Sized for
 * the largest payload a publish can store: the scaled budget below outruns this
 * past ~675 MB, so this is what a multi-GB run actually gets — 2 GB inside 45
 * minutes is ~760 KB/s sustained.
 */
const RUN_FETCH_TIMEOUT_MAX_MS = 45 * 60_000;
/**
 * Assumed floor throughput (bytes/ms ≈ 250 KB/s) used to scale a download's
 * timeout by its known size. A flat timeout either kills legitimate large
 * downloads or lets small stalled ones hang.
 */
const RUN_FETCH_MIN_THROUGHPUT_BYTES_PER_MS = 250;

/**
 * Refuse to download a payload larger than this. Mirrors the server's highest
 * store ceiling — a dataset publish's — so it only trips on runs stored before
 * the cap existed; failing with a clear message beats crashing the tab.
 */
const RUN_DOWNLOAD_MAX_BYTES = MAX_PUBLISH_RESULT_BYTES;

/**
 * Ceiling for the fallback that has to hold the whole payload in the JS heap
 * (no OPFS to stream into). Far below {@link RUN_DOWNLOAD_MAX_BYTES}, because
 * that path allocates one contiguous buffer of the entire file: a multi-GB
 * `arrayBuffer()` either throws or takes the tab with it, so refuse it up front
 * with a message that names the real constraint.
 */
const HEAP_FALLBACK_MAX_BYTES = MAX_RESULT_BYTES;

/**
 * Timeout budget for downloading `sizeBytes` of Arrow, clamped to
 * [{@link RUN_FETCH_TIMEOUT_MIN_MS}, {@link RUN_FETCH_TIMEOUT_MAX_MS}].
 * Unknown sizes (legacy runs) get the floor.
 */
export const runFetchTimeoutMs = (sizeBytes: number | null): number => {
  if (sizeBytes == null || sizeBytes <= 0) return RUN_FETCH_TIMEOUT_MIN_MS;
  const budget = sizeBytes / RUN_FETCH_MIN_THROUGHPUT_BYTES_PER_MS;
  return Math.min(
    RUN_FETCH_TIMEOUT_MAX_MS,
    Math.max(RUN_FETCH_TIMEOUT_MIN_MS, Math.ceil(budget)),
  );
};

/** Resolver for obtaining a signed Arrow URL for a run's immutable result. */
export type RunPayloadUrlResolver = (
  runId: string,
  signal: AbortSignal,
) => Promise<{ url: string; sizeBytes?: number | null }>;

/** Segments payload cache entries by authorization surface. */
export type RunPayloadScope = "org" | `public:${string}` | `shared:${string}`;

/** Classified failure modes for ensuring a run payload. */
export type RunPayloadErrorReason =
  | "aborted"
  | "expired"
  | "timeout"
  | "network"
  | "too_large"
  | "quota"
  | "duckdb"
  | "unknown";

type RunPayloadErrorContext = {
  runId?: string;
  scope?: RunPayloadScope;
};

/**
 * Typed error for the run payload pipeline. Callers use `reason` (and the
 * `isExpectedPayloadError` helper) to decide whether a failure is an expected,
 * recoverable condition (abort, expired result, transient network) or a genuine
 * bug worth surfacing at `error` level.
 */
export class RunPayloadError extends Error {
  readonly reason: RunPayloadErrorReason;
  readonly runId?: string;
  readonly scope?: RunPayloadScope;

  constructor(
    reason: RunPayloadErrorReason,
    message: string,
    context?: RunPayloadErrorContext,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = "RunPayloadError";
    this.reason = reason;
    this.runId = context?.runId;
    this.scope = context?.scope;
  }
}

const EXPECTED_PAYLOAD_REASONS: ReadonlySet<RunPayloadErrorReason> = new Set([
  "aborted",
  "expired",
  "timeout",
  "network",
  // Storage pressure is environmental, and the failing write already evicted
  // older entries — a retry is expected to succeed.
  "quota",
]);

/** Returns true for AbortError/TimeoutError/cancellation-style errors. */
const isAbortLike = (err: unknown): boolean => {
  if (err instanceof DOMException) {
    return err.name === "AbortError" || err.name === "TimeoutError";
  }
  if (!(err instanceof Error)) return false;
  return /aborterror|timeouterror|cancellederror|cancelederror/i.test(
    `${err.name}:${err.message}`,
  );
};

/**
 * Classifies whether a payload failure is an expected, recoverable condition
 * (abort, expired/missing result, transient network/timeout) vs. an unexpected
 * bug. Used to keep background-warm logging out of Sentry.
 */
export const isExpectedPayloadError = (err: unknown): boolean => {
  if (err instanceof RunPayloadError) {
    return EXPECTED_PAYLOAD_REASONS.has(err.reason);
  }
  return isAbortLike(err);
};

/** Result metadata for a successfully ensured run payload query. */
export type RunPayloadResult = {
  runId: string;
  scope: RunPayloadScope;
  source: "opfs_hit" | "inline_bytes" | "network_fetch";
  byteLength: number;
};

type RunPayloadQueryArgs = {
  runId: string;
  scope: RunPayloadScope;
  urlResolver?: RunPayloadUrlResolver;
  /** Pre-fetched Arrow bytes (e.g. inline draft-preview result). */
  inlineBytes?: Uint8Array;
};

type EnsureRunPayloadArgs = {
  runId: string;
  signal?: AbortSignal;
  scope?: RunPayloadScope;
  urlResolver?: RunPayloadUrlResolver;
  inlineBytes?: Uint8Array;
};

const defaultUrlResolver: RunPayloadUrlResolver = (runId, signal) =>
  trpc.runs.getResultUrl.query({ runId }, { signal });

/**
 * Stable query key for a run's payload. Because runs are immutable and
 * content-addressed by id, the `runId` is the complete freshness boundary;
 * `scope` isolates auth contexts.
 */
export const runPayloadQueryKey = ({
  runId,
  scope,
}: {
  runId: string;
  scope: RunPayloadScope;
}) => [RUN_PAYLOAD_QUERY_PREFIX, runId, scope] as const;

const parseRunPayloadQueryKey = (
  queryKey: QueryKey,
): { runId: string; scope: RunPayloadScope } | null => {
  if (!Array.isArray(queryKey)) return null;
  if (queryKey[0] !== RUN_PAYLOAD_QUERY_PREFIX) return null;
  const runId = queryKey[1];
  const scope = queryKey[2];
  if (typeof runId !== "string") return null;
  if (typeof scope !== "string") return null;
  return { runId, scope: scope as RunPayloadScope };
};

/**
 * Opens the run's Arrow download. Returns the still-unread response so callers
 * can stream it to OPFS instead of buffering, plus the recorded size (used for
 * the download gate, the timeout budget, and cache space reservation).
 */
const openRunArrowResponse = async ({
  runId,
  signal,
  urlResolver,
}: {
  runId: string;
  signal: AbortSignal;
  urlResolver: RunPayloadUrlResolver;
}): Promise<{ response: Response; sizeBytes: number | null }> => {
  let url: string;
  let sizeBytes: number | null = null;
  try {
    ({ url, sizeBytes = null } = await urlResolver(runId, signal));
  } catch (err) {
    throw classifyUrlResolverError(err, runId, signal);
  }
  throwIfAborted(signal);

  // Fail before allocating, rather than crashing the tab mid-download. Without
  // OPFS the payload cannot be streamed anywhere and has to be held whole in
  // the heap, so that browser gets the much lower ceiling.
  const canStreamToDisk = isRunCacheSupported();
  const limit = canStreamToDisk
    ? RUN_DOWNLOAD_MAX_BYTES
    : HEAP_FALLBACK_MAX_BYTES;
  if (sizeBytes != null && sizeBytes > limit) {
    throw new RunPayloadError(
      "too_large",
      `Run result is too large to load in the browser (${formatBytes(
        sizeBytes,
      )}, limit ${formatBytes(limit)})${
        canStreamToDisk
          ? ""
          : " — this browser cannot cache results to disk, which lowers the limit"
      }`,
      { runId },
    );
  }

  const fetchSignal = combineSignals(
    signal,
    AbortSignal.timeout(runFetchTimeoutMs(sizeBytes)),
  );
  let response: Response;
  try {
    response = await fetch(url, { signal: fetchSignal });
  } catch (err) {
    throw classifyFetchError(err, runId, signal);
  }
  if (!response.ok) {
    const reason: RunPayloadErrorReason =
      response.status === 404 ? "expired" : "network";
    throw new RunPayloadError(
      reason,
      `Run result fetch failed (${response.status})`,
      { runId },
    );
  }
  return { response, sizeBytes };
};

/**
 * Maps a result-URL resolver rejection to a typed `RunPayloadError`. A server
 * `NOT_FOUND` (missing object / expired TTL) is an expected `expired`; a caller
 * abort is `aborted`; anything else is treated as `network`.
 */
const classifyUrlResolverError = (
  err: unknown,
  runId: string,
  signal: AbortSignal,
): RunPayloadError => {
  if (signal.aborted || isAbortLike(err)) {
    return new RunPayloadError("aborted", "Run payload load aborted", {
      runId,
    });
  }
  const message = err instanceof Error ? err.message : String(err);
  if (/expired|not found|no result|not stored/i.test(message)) {
    return new RunPayloadError("expired", message, { runId }, { cause: err });
  }
  return new RunPayloadError(
    "network",
    `Failed to resolve run result URL: ${message}`,
    { runId },
    { cause: err },
  );
};

/**
 * Maps a `fetch` rejection to a typed `RunPayloadError`: caller abort vs. the
 * size-scaled timeout vs. a transient network failure.
 */
const classifyFetchError = (
  err: unknown,
  runId: string,
  signal: AbortSignal,
): RunPayloadError => {
  if (signal.aborted) {
    return new RunPayloadError("aborted", "Run payload load aborted", {
      runId,
    });
  }
  if (err instanceof DOMException && err.name === "TimeoutError") {
    return new RunPayloadError(
      "timeout",
      "Run payload fetch timed out",
      { runId },
      { cause: err },
    );
  }
  if (isAbortLike(err)) {
    return new RunPayloadError("aborted", "Run payload load aborted", {
      runId,
    });
  }
  return new RunPayloadError(
    "network",
    "Run payload fetch failed",
    { runId },
    { cause: err },
  );
};

/**
 * Maps a streaming-download failure to a typed `RunPayloadError`: caller abort,
 * an exhausted storage quota, or a mid-stream network drop.
 */
const classifyStreamError = (
  err: unknown,
  runId: string,
  signal: AbortSignal,
): RunPayloadError => {
  if (signal.aborted || isAbortLike(err)) {
    return classifyFetchError(err, runId, signal);
  }
  if (
    err instanceof DOMException &&
    (err.name === "QuotaExceededError" || err.code === 22)
  ) {
    return new RunPayloadError(
      "quota",
      "Ran out of browser storage while caching the run payload",
      { runId },
      { cause: err },
    );
  }
  return new RunPayloadError(
    "network",
    "Run payload download failed",
    { runId },
    { cause: err },
  );
};

/** Registers Arrow bytes in DuckDB, wrapping failures as a `duckdb` error. */
const registerRunBuffer = async ({
  runId,
  scope,
  bytes,
}: {
  runId: string;
  scope: RunPayloadScope;
  bytes: Uint8Array;
}): Promise<void> => {
  try {
    await duckdb.registerRunBuffer(runId, bytes);
  } catch (err) {
    throw new RunPayloadError(
      "duckdb",
      "Failed to register run payload in DuckDB",
      { runId, scope },
      { cause: err },
    );
  }
};

/**
 * Registers a run's OPFS-cached payload with DuckDB by file handle, so the
 * bytes are never copied into the JS or WASM heap.
 */
const registerRunFile = async ({
  runId,
  scope,
  cached,
}: {
  runId: string;
  scope: RunPayloadScope;
  cached: CachedRunArrow;
}): Promise<void> => {
  try {
    await duckdb.registerRunFile(runId, cached);
  } catch (err) {
    throw new RunPayloadError(
      "duckdb",
      "Failed to register run payload in DuckDB",
      { runId, scope },
      { cause: err },
    );
  }
};

const loadRunPayload = async (
  context: QueryFunctionContext<ReturnType<typeof runPayloadQueryKey>>,
  args: { urlResolver?: RunPayloadUrlResolver; inlineBytes?: Uint8Array },
): Promise<RunPayloadResult> => {
  const parsed = parseRunPayloadQueryKey(context.queryKey);
  if (!parsed) {
    throw new Error("Invalid run payload query key");
  }
  const { runId, scope } = parsed;

  // Inline bytes handed to us directly (e.g. a draft-preview execute response):
  // persist to OPFS + register without any network round trip.
  if (args.inlineBytes) {
    await writeRunArrowToOpfs(runId, args.inlineBytes);
    await registerRunBuffer({ runId, scope, bytes: args.inlineBytes });
    return {
      runId,
      scope,
      source: "inline_bytes",
      byteLength: args.inlineBytes.byteLength,
    };
  }

  const cached = await getCachedRunArrow(runId);
  if (cached) {
    await registerRunFile({ runId, scope, cached });
    return {
      runId,
      scope,
      source: "opfs_hit",
      byteLength: cached.file.size,
    };
  }

  throwIfAborted(context.signal);
  const { response, sizeBytes } = await openRunArrowResponse({
    runId,
    signal: context.signal,
    urlResolver: args.urlResolver ?? defaultUrlResolver,
  });

  // Without OPFS (e.g. Safari private mode) there is nowhere to stream to, so
  // fall back to buffering the payload in the JS heap.
  if (!isRunCacheSupported() || !response.body) {
    // `openRunArrowResponse` already applied this ceiling when it knew OPFS was
    // missing; this catches the other way into the same allocation, a response
    // with no readable body, which it cannot predict from the size alone.
    if (sizeBytes != null && sizeBytes > HEAP_FALLBACK_MAX_BYTES) {
      throw new RunPayloadError(
        "too_large",
        `Run result is too large to load without a disk cache (${formatBytes(
          sizeBytes,
        )}, limit ${formatBytes(HEAP_FALLBACK_MAX_BYTES)})`,
        { runId, scope },
      );
    }
    const bytes = new Uint8Array(await response.arrayBuffer());
    await registerRunBuffer({ runId, scope, bytes });
    return {
      runId,
      scope,
      source: "network_fetch",
      byteLength: bytes.byteLength,
    };
  }

  let byteLength: number;
  try {
    byteLength = await streamRunArrowToOpfs(runId, response.body, {
      expectedBytes: sizeBytes,
    });
  } catch (err) {
    throw classifyStreamError(err, runId, context.signal);
  }
  logger.debug(
    { event: "network_fetch", runId, scope, byteLength },
    "run payload cache event",
  );

  const stored = await getCachedRunArrow(runId);
  if (!stored) {
    throw new RunPayloadError(
      "quota",
      "Run payload was not readable after download",
      { runId, scope },
    );
  }
  await registerRunFile({ runId, scope, cached: stored });
  return {
    runId,
    scope,
    source: "network_fetch",
    byteLength,
  };
};

/** Query options factory for run payload loading and DuckDB registration. */
export const runPayloadQueryOptions = ({
  runId,
  scope,
  urlResolver,
  inlineBytes,
}: RunPayloadQueryArgs) =>
  queryOptions({
    queryKey: runPayloadQueryKey({ runId, scope }),
    queryFn: (context) => loadRunPayload(context, { urlResolver, inlineBytes }),
    // Runs are immutable, so a loaded payload is never stale; released by
    // gcTime once no consumer observes it.
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: RUN_PAYLOAD_GC_MS,
  });

const waitForPromiseWithSignal = async <T>(
  promise: Promise<T>,
  signal?: AbortSignal,
): Promise<T> => {
  if (!signal) return promise;
  if (signal.aborted) throw makeAbortError(signal);
  return await new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(makeAbortError(signal));
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener("abort", onAbort);
        reject(error);
      },
    );
  });
};

/**
 * Imperative payload ensure helper for non-React action modules. Loads and
 * registers the run's immutable Arrow result into DuckDB, keyed by `runId`.
 */
export const ensureRunPayload = async ({
  runId,
  signal,
  scope = "org",
  urlResolver,
  inlineBytes,
}: EnsureRunPayloadArgs): Promise<RunPayloadResult> => {
  const queryPromise = queryClient.ensureQueryData(
    runPayloadQueryOptions({ runId, scope, urlResolver, inlineBytes }),
  );
  return await waitForPromiseWithSignal(queryPromise, signal);
};

/**
 * Seeds the run-payload cache from pre-fetched inline Arrow bytes (e.g. a
 * draft-preview execute response), writing OPFS + registering DuckDB so a
 * subsequent `ensureRunPayload` resolves without a GCS round trip.
 */
export const seedRunPayload = async (
  runId: string,
  bytes: Uint8Array,
  scope: RunPayloadScope = "org",
): Promise<RunPayloadResult> =>
  await queryClient.ensureQueryData(
    runPayloadQueryOptions({ runId, scope, inlineBytes: bytes }),
  );

const makeAbortError = (signal: AbortSignal): Error => {
  const reason = signal.reason;
  if (reason instanceof Error) return reason;
  return new DOMException("The operation was aborted.", "AbortError");
};

const throwIfAborted = (signal: AbortSignal) => {
  if (signal.aborted) throw makeAbortError(signal);
};

const combineSignals = (a: AbortSignal, b: AbortSignal): AbortSignal => {
  if (typeof AbortSignal !== "undefined" && "any" in AbortSignal) {
    return (
      AbortSignal as { any: (signals: AbortSignal[]) => AbortSignal }
    ).any([a, b]);
  }
  const controller = new AbortController();
  const abortFrom = (signal: AbortSignal) => () => {
    if (controller.signal.aborted) return;
    if (signal.reason !== undefined) controller.abort(signal.reason);
    else controller.abort();
  };
  if (a.aborted) abortFrom(a)();
  else a.addEventListener("abort", abortFrom(a), { once: true });
  if (b.aborted) abortFrom(b)();
  else b.addEventListener("abort", abortFrom(b), { once: true });
  return controller.signal;
};
